import React from 'react';
import StatusTimeline from '../timeline/StatusTimeline';

/**
 * OrderMessage Component
 * 
 * Renders special order messages with detailed order information, escrow steps, and transaction status
 * Includes platform-specific icons and status indicators
 * 
 * @param {Object} msg - Order message object
 * @param {number} idx - Message index
 * @param {boolean} isDarkMode - Dark mode state
 * @returns {JSX.Element} Order message component
 */
const OrderMessage = ({ msg, idx, isDarkMode }) => {
  const statusRaw = msg.status || '';
  const status = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1).toLowerCase();
  const orderId = msg.order_id || msg.orderId || '';
  const productName = msg.product_name || msg.productName || 'Product';
  const orderDate = msg.order_date || msg.date || '';

  // Helper function to get transaction status text
  const getTransactionStatusText = (status, category = '') => {
    if (!status) return '';
    const s = status.toLowerCase().replace(/[\s_-]/g, ''); // normalize: remove spaces, hyphens, underscores
    if (s === 'onhold') return 'Your payment is under checking. Once verified, your order will move to processing. No action needed — we\'ll update you soon.';
    if (s === 'processing' || s === 'inprogress') return 'We\'ve received your payment and informed the seller to transfer the account to us. Your order is now being processed.';
    if (s === 'nuwanisuru') {
      // Check if this is a Facebook category order
      if (category.toLowerCase().includes('facebook')) {
        return 'The seller has assigned the escrow agent as a page admin, and the escrow agent will review everything and remove other admins. We will contact you as soon as it is ready to be released.';
      }
      return 'Seller has been transferred account to our escrow agent, who is now reviewing and securing the login details. We\'ll contact you shortly once it\'s ready for release.';
    }
    if (s === 'nuwanis') {
      // Check if this is a Facebook category order
      if (category.toLowerCase().includes('facebook')) {
        return 'The escrow agent has transferred administrative rights to you. Please accept invite and confirm within 24 hours. If we don\'t hear from you within 24 hours, we\'ll consider the order complete and release payment to the seller.';
      }
      return (
        <>
          The account details have been successfully transferred to you.
          Please check the login info box and confirm within 24 hours. If we don't hear from you within 24 hours, we'll consider the order complete and release payment to the seller.
          <br /><br />
          <span className="font-bold text-black">
            Buyer, we will change e-mail, change 2 step verification settings and delete all connected devices. So, we recommend to wait 1/2 days to be sure that nothing happen. Sometimes accounts may get locked due to a lot of security changes
          </span>
        </>
      );
    }
    if (s === 'completed' || s === 'complete') return 'The transaction has been completed successfully! 🎉\nWe\'d really appreciate it if you could take a moment to leave a review about your experience with us.\nYour feedback helps us grow and serve you better!';
    if (s === 'refunded') return 'Dear buyer, your refund has been processed successfully. The amount will reflect in your account within 3–5 business days. Let us know if you need any further assistance.';
    return '';
  };

  return (
    <div key={msg.id || idx} className="mb-4 w-full bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 px-6 py-4">
      {/* Order Info Row */}
      <div className="w-full mb-4">
        <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-700/50">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="12" fill="#FF0000"/>
              <polygon points="10,8 16,12 10,16" fill="white"/>
            </svg>
          </div>
          <div className="flex-1">
            <div className="font-bold text-xl text-gray-900">YTproud Marketplace</div>
            <div className="text-sm text-gray-500">Order Details</div>
          </div>
          <div className={`text-sm px-4 py-2 rounded-xl ${
            isDarkMode ? 'text-gray-300 bg-gray-800' : 'text-gray-500 bg-gray-50'
          }`}>{orderDate ? orderDate : ''}</div>
        </div>
        
        <div className={`px-6 py-6 mb-4 rounded-2xl shadow-sm flex flex-wrap gap-6 items-center justify-between ${
          isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f]' : 'bg-gradient-to-br from-gray-50 to-white'
        }`}>
          {/* Order ID */}
          <div className="flex items-center gap-3 min-w-[150px]">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </span>
            <div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Order ID</div>
              <div className={`font-semibold flex items-center gap-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-800'
              }`}>
                {orderId}
              </div>
            </div>
          </div>
          
          {/* Product */}
          <div className="flex items-center gap-3 min-w-[150px]">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </span>
            <div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Product</div>
              <div className={`font-semibold flex items-center gap-2 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-800'
              }`}>
                <img src={msg.product_image || 'https://img.icons8.com/color/48/000000/youtube-play.png'} alt="Product" className="w-8 h-8 rounded-lg shadow border" />
                {productName}
              </div>
            </div>
          </div>
          
          {/* Category */}
          <div className="flex items-center gap-3 min-w-[150px]">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-md">
              {(() => {
                const category = (msg.category_name || msg.product_category || '').toLowerCase();
                if (category.includes('youtube')) {
                  return (
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  );
                } else if (category.includes('tiktok')) {
                  return (
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  );
                } else if (category.includes('facebook')) {
                  return (
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  );
                } else if (category.includes('instagram')) {
                  return (
                    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                  );
                } else {
                  return (
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  );
                }
              })()}
            </span>
            <div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Category</div>
              <div className={`font-semibold ${
                isDarkMode ? 'text-gray-300' : 'text-gray-800'
              }`}>
                {msg.category_name || 'Uncategorized'}
              </div>
            </div>
          </div>
          
          {/* Payment */}
          <div className="flex items-center gap-3 min-w-[150px]">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/></svg>
            </span>
            <div>
              <div className="text-sm text-gray-500">Payment</div>
              {(() => {
                const s = (statusRaw || '').toLowerCase().replace(/[-_ ]/g, '');
                if (s === 'onhold') {
                  return (
                    <span className="font-semibold text-base text-black">Checking</span>
                  );
                }
                if (s === 'refunded') {
                  return (
                    <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-base">
                      {/* Refund/Rotate Icon */}
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refund
                    </span>
                  );
                }
                // Default: Paid
                return (
                  <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-base">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#22c55e"/><path d="M8 12l2 2 4-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Paid
                  </span>
                );
              })()}
            </div>
          </div>
          
          {/* Amount */}
          <div className="flex items-center gap-3 min-w-[150px]">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 text-white shadow-md">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <div>
              <div className="text-sm text-gray-500">Amount</div>
              <span className="text-gray-800 font-semibold text-base">
                ${msg.product_price || msg.amount || '0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* How Escrow Works Box */}
      <div className="w-full">
        <div className={`rounded-2xl shadow-xl p-8 mt-4 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#1a1a1a] border border-red-800/50' 
            : 'bg-gradient-to-br from-red-50 via-white to-gray-50 border border-red-100'
        }`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-md">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/></svg>
            </div>
            <span className={`font-extrabold text-2xl tracking-tight ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>How Escrow Works</span>
          </div>
          {(() => {
            const category = (msg.category_name || msg.product_category || '').toLowerCase();
            const stepCircle = (n) => (
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white font-bold text-lg shadow">{n}</span>
            );
            const stepTextClass = isDarkMode ? 'text-gray-300' : 'text-gray-700';
            const italicTextClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
            
            if (category.includes('youtube')) {
              return (
                <ol className="space-y-5 pl-2">
                  <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer make a purchase , the agent will notify the seller to add the agent's email address as the owner.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className={`${stepTextClass} text-base font-medium`}>The seller designates the escrow agent as owner.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className={`${stepTextClass} text-base font-medium`}>The escrow agent verifies everything and assigns manager rights to the buyer.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className={`${stepTextClass} text-base font-medium`}>After 7 days, the escrow agent remove all the other managers and assigns primary ownership rights to the buyer <span className={`italic ${italicTextClass}`}>(7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)</span></span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className={`${stepTextClass} text-base font-medium`}>After completing the transaction, the money is sent by the agent to the seller</span></li>
                </ol>
              );
            } else if (category.includes('facebook')) {
              return (
                <ol className="space-y-5 pl-2">
                  <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer make a purchase , the agent will notify the seller assigns the escrow agent's page as the administrator.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className={`${stepTextClass} text-base font-medium`}>After the seller assigns the Escrow agent as a page administrator, the Escrow agent checks everything and removes other administrators.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className={`${stepTextClass} text-base font-medium`}>Then, Escrow agent adds buyer Facebook account as administrator.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className={`${stepTextClass} text-base font-medium`}>The buyer must check and confirm it with the agent after logging into the Facebook page.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className={`${stepTextClass} text-base font-medium`}>After confirming the buyer and then remove the agent's account and completing the transaction, the money will be sent by the agent to the seller.</span></li>
                </ol>
              );
            } else if (category.includes('instagram')) {
              return (
                <ol className="space-y-5 pl-2">
                  <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer make a purchase , the agent will notify the seller to transfers login details and the original email account to the escrow agent.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className={`${stepTextClass} text-base font-medium`}>The escrow agent checks everything, changes the login details.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className={`${stepTextClass} text-base font-medium`}>After that ,the login details are sent to the buyer by the agent.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer has logged into the account, checking the account and confirming it with the agent</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className={`${stepTextClass} text-base font-medium`}>After completing the transaction, the money is sent by the agent to the seller</span></li>
                </ol>
              );
            } else if (category.includes('tiktok')) {
              return (
                <ol className="space-y-5 pl-2">
                  <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer make a purchase , the agent will notify the seller to transfers login details and the original email account to the escrow agent.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className={`${stepTextClass} text-base font-medium`}>The escrow agent checks everything, changes the login details.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className={`${stepTextClass} text-base font-medium`}>After that ,the login details are sent to the buyer by the agent.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer has logged into the account, checking the account and confirming it with the agent</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className={`${stepTextClass} text-base font-medium`}>After completing the transaction, the money is sent by the agent to the seller</span></li>
                </ol>
              );
            } else {
              // Default: YouTube steps
              return (
                <ol className="space-y-5 pl-2">
                  <li className="flex items-start gap-4"><span>{stepCircle(1)}</span><span className={`${stepTextClass} text-base font-medium`}>After the buyer make a purchase , the agent will notify the seller to add the agent's email address as the owner.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(2)}</span><span className={`${stepTextClass} text-base font-medium`}>The seller designates the escrow agent as owner.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(3)}</span><span className={`${stepTextClass} text-base font-medium`}>The escrow agent verifies everything and assigns manager rights to the buyer.</span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(4)}</span><span className={`${stepTextClass} text-base font-medium`}>After 7 days, the escrow agent remove all the other managers and assigns primary ownership rights to the buyer <span className={`italic ${italicTextClass}`}>(7 days is the minimum amount of time required in order to assign a new primary owner in the control panel.)</span></span></li>
                  <li className="flex items-start gap-4"><span>{stepCircle(5)}</span><span className={`${stepTextClass} text-base font-medium`}>After completing the transaction, the money is sent by the agent to the seller</span></li>
                </ol>
              );
            }
          })()}
        </div>
      </div>
      
      {/* Transaction Status Box */}
      <div className="w-full mt-4">
        <div className={`rounded-2xl shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#1a1a1a] border border-gray-700/50' 
            : 'bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className={`font-bold text-lg flex items-center gap-2 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span>Transaction Status</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Remove button background and text for Paid. Only show text for Checking and Refund. */}
              {(() => {
                const s = (statusRaw || '').toLowerCase().replace(/[-_ ]/g, '');
                if (s === 'onhold') return <span className={`font-semibold text-base ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>Checking</span>;
                if (s === 'refunded') return <span className="font-semibold text-base text-blue-600">Refund</span>;
                // For Paid statuses, show nothing
                return null;
              })()}
            </div>
          </div>
          <div className={`rounded-xl p-4 border shadow-sm ${
            isDarkMode 
              ? 'bg-[#0f0f0f] border-gray-700/50' 
              : 'bg-white border-gray-100'
          }`}>
            <div className={`text-base leading-relaxed ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {getTransactionStatusText(statusRaw, msg.category_name || msg.product_category)}
            </div>
          </div>
          {/* Status Timeline */}
          <StatusTimeline 
            status={statusRaw} 
            isDarkMode={isDarkMode} 
          />
        </div>
      </div>
    </div>
  );
};

export default OrderMessage;
